import React, { useState } from 'react';
import { Gift, Zap, Sparkles } from 'lucide-react';
import Axios from '@/services/axios';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@components/ui/dialog';
import { useAuthContext } from '@context/useAuthStore';
import { useNavigate } from 'react-router-dom';

function CodeActivation({ setIsLoading }) {
  const navigate = useNavigate();
  const axios = Axios;
  const { refetchSubscription } = useAuthContext();
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [activationCode, setActivationCode] = useState('');

  const activateSubscription = async () => {
    try {
      const data = {
        subscription_code: activationCode,
      };
      setIsLoading(true);
      await axios.post('/user/subscription/activate', data);
      await refetchSubscription();
      navigate('/home');
    } catch (error) {
      alert('An error occurred while activating your plan. Please try again later.');
    }
    setIsLoading(false);
  };

  return (
    <Dialog>
      <div className="text-center">
        <DialogTrigger asChild>
          <button className="inline-flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-medium underline underline-offset-4 transition-colors">
            <Sparkles className="w-4 h-4" />
            Have a promo code? Click here to activate
          </button>
        </DialogTrigger>
      </div>
      <DialogContent className="max-w-xl">
        <DialogTitle>Got a code?</DialogTitle>
        <DialogDescription>Paste your code here to activate your account</DialogDescription>
        <div className="mb-12">
          {!showCodeInput ? (
            <div className="max-w-sm mx-auto">
              <div className="group relative overflow-hidden rounded-2xl p-[1px] bg-gradient-to-r from-primary via-primary/80 to-accent">
                <div className="bg-card rounded-2xl p-6 text-center backdrop-blur-sm">
                  <div className="relative">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center shadow-lg">
                      <Gift className="w-8 h-8 text-primary-foreground" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-accent rounded-full animate-pulse"></div>
                  </div>
                  <h3 className="text-xl font-bold text-card-foreground mb-2">Got an Activation Code?</h3>
                  <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                    Unlock premium features instantly with your special activation code
                  </p>
                  <button
                    onClick={() => setShowCodeInput(true)}
                    className="group w-full bg-gradient-to-r from-primary to-accent text-primary-foreground py-3 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-primary/25 hover:-translate-y-0.5 active:translate-y-0">
                    <span className="flex items-center justify-center gap-2">
                      <Zap className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                      Enter Code
                    </span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-sm mx-auto">
              <div className="relative overflow-hidden rounded-2xl p-[1px] bg-gradient-to-r from-primary via-primary/80 to-accent">
                <div className="bg-card rounded-2xl p-6 backdrop-blur-sm">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center shadow-lg">
                      <Gift className="w-8 h-8 text-primary-foreground" />
                    </div>
                    <h3 className="text-xl font-bold text-card-foreground mb-2">Activate Your Account</h3>
                    <p className="text-muted-foreground text-sm">Enter your activation code below to get started</p>
                  </div>

                  <div className="space-y-4">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="XXX-XXXXX-XXXXX"
                        value={activationCode}
                        onChange={(e) => setActivationCode(e.target.value.toUpperCase())}
                        className="w-full px-4 py-3 bg-background border-2 border-border rounded-xl text-center font-mono tracking-[0.2em] focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 transition-all placeholder:text-muted-foreground"
                        maxLength="20"
                      />
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={activateSubscription}
                        disabled={!activationCode.trim()}
                        className="flex-1 bg-gradient-to-r from-primary to-accent text-primary-foreground py-3 px-4 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-primary/25 hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none disabled:cursor-not-allowed active:translate-y-0">
                        Activate Now
                      </button>
                      <button
                        onClick={() => {
                          setShowCodeInput(false);
                          setActivationCode('');
                        }}
                        className="px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CodeActivation;
